import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusCircleFilled,
} from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Space, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib/form';
import moment from 'moment';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { fetchUsers } from '../../../api/users';
import { GroupDetails, UserDetails } from '../../../constants/types';
import {
  supervisorGroupConstant,
  userGroupConstant,
} from '../../../constants/userGroup';
import theme from '../../../styles/theme';
import {
  CheckboxContainer,
  StyledCheckboxItem,
  StyledFormItem,
} from '../style';
import SelectUserModal from './SelectUserModal';
import { StyledAddBtn } from './style';

const { TextArea } = Input;

interface Props {
  loading: boolean;
  onSubmit: Function;
  cancel: Function;
}

interface UserList {
  email?: string;
  label: string;
  value: string | number;
  isChecked: boolean;
  type: 'New' | 'Old';
  enabled?: boolean;
  account_end_date?: string;
  user_details?: UserDetails;
}

const AddGroupForm: FC<Props> = ({ loading, onSubmit, cancel }) => {
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const intl = useIntl();
  const [userList, setUserList] = useState<UserList[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserList[]>([]);
  const [isSelectUsersModal, setIsSelectUsersModal] = useState<boolean>(false);

  useEffect(() => {
    fetchUsers().then(users => {
      const activeUser = users.filter(
        item =>
          item.enabled !== false &&
          (item?.['attributes']?.['account_end_date']?.[0]
            ? moment(
                parseInt(item['attributes']['account_end_date'][0], 10),
              ).valueOf() > moment().valueOf()
            : true),
      );
      const userListDetails = activeUser.map((user: UserDetails) => ({
        email: user.email,
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
        isChecked: false,
        type: 'New',
        enabled: user.enabled,
        account_end_date: user?.['attributes']?.['account_end_date']?.[0],
        user_details: user,
      })) as UserList[];
      setUserList([...userListDetails]);
      setSelectedUsers([]);
    });
    return () => {
      setUserList([]);
      setSelectedUsers([]);
    };
  }, []);

  const onRemove = (index: number) => {
    const data = selectedUsers;
    const userListData: UserList[] = [...userList];
    const userListDataIndex = userListData.findIndex(
      user => user.value === data[index].value,
    );

    if (userListDataIndex !== -1) {
      userListData[userListDataIndex].isChecked = false;
      setUserList([...userListData]);
    }
    data.splice(index, 1);
    setSelectedUsers([...data]);
  };

  const onSelect = (selectedUser: any[]) => {
    const selectedUserId = selectedUser.map((user: UserList) => user.value);
    const userListData: UserList[] = [...userList];
    const updatedData = userListData.map(user => {
      if (!selectedUserId.includes(user.value)) {
        user.isChecked = false;
        return user;
      }
      return user;
    });
    setUserList([...updatedData]);
    setSelectedUsers([...selectedUser]);
    setIsSelectUsersModal(false);
  };

  const onCloseSelectUserModal = () => {
    setIsSelectUsersModal(false);
  };

  const onFinish = (groupDetails: GroupDetails) => {
    const { name, description } = groupDetails;
    const addedUser = userList
      .filter(user => user.type === 'New' && user.isChecked)
      .map(user => {
        return {
          id: user.value,
          account_end_date: user.account_end_date,
          user_details: user.user_details,
        };
      });

    onSubmit({
      name: name.trim(),
      path: `/user-management/groups/${name?.replace(/ /g, '-').trim()}`,
      subGroups: [],
      description,
      deleteUser: [],
      addedUser,
    });
  };

  return (
    <>
      <Form
        layout="vertical"
        onFinish={onFinish}
        ref={formRef}
        validateTrigger="onBlur"
        form={form}
        data-testid="create-group-form"
      >
        <Row gutter={15}>
          <Col span={24}>
            <StyledFormItem
              data-testid="usergroup-name"
              label={
                <>
                  <FormattedMessage id="UserGroup.form.label.userGroupName" />
                  <Tooltip
                    title={
                      <FormattedMessage id="UserGroup.form.label.userGroupName.tooltip" />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </>
              }
              name="name"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'UserGroup.form.userGroupName.required',
                  }),
                },
                {
                  validator: async (_rule, value) => {
                    if (!value) return;

                    if (
                      !(
                        value.startsWith(userGroupConstant) ||
                        value.startsWith(supervisorGroupConstant)
                      )
                    ) {
                      throw new Error(
                        intl.formatMessage({
                          id: 'UserGroup.form.userGroupName.format',
                        }),
                      );
                    }

                    const colonCount = (value.match(/:/g) || []).length;
                    if (colonCount > 1) {
                      throw new Error(
                        intl.formatMessage({
                          id: 'UserGroup.form.userGroupName.onecolonallowed',
                        }),
                      );
                      // Optionally replace above string with a localized message ID if needed
                    }
                  },
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'UserGroup.form.placeholder.userGroupName',
                })}
                data-testid="group-name"
                maxLength={200}
                onBlur={e => {
                  const rawValue = e.target.value || '';
                  const cleaned = rawValue
                    .replace(/\s*:\s*/g, ':')
                    .replace(/\s+/g, ' ')
                    .trim()
                    .toLowerCase();
                  form.setFieldsValue({ name: cleaned });
                }}
              />
            </StyledFormItem>
          </Col>
          <Col span={24}>
            <StyledFormItem
              data-testid="usergroup-description"
              label={<FormattedMessage id="UserGroup.form.label.description" />}
              name="description"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'UserGroup.form.description.required',
                  }),
                },
              ]}
            >
              <TextArea
                placeholder={intl.formatMessage({
                  id: 'UserGroup.form.placeholder.description',
                })}
                data-testid="group-description"
                maxLength={300}
              />
            </StyledFormItem>
          </Col>
          <Col span={24} style={{ marginBottom: '22px' }}>
            <StyledFormItem
              className="only-label"
              label={<FormattedMessage id="UserGroup.form.label.manageUsers" />}
              labelAlign="left"
              colon={false}
            />
            <StyledAddBtn
              shape="round"
              size="large"
              type="default"
              icon={<PlusCircleFilled />}
              className="action-btn"
              onClick={() => setIsSelectUsersModal(true)}
              data-testid="group-add-users"
            >
              <FormattedMessage id="Global.Add" />
            </StyledAddBtn>
          </Col>
          <Col span={24}>
            <CheckboxContainer data-testid="group-users">
              {selectedUsers.length > 0 &&
                selectedUsers.map((user: any, index: number) => (
                  <StyledCheckboxItem key={index}>
                    {user.label}&nbsp;
                    {user.email && `| ${user.email}`}
                    <DeleteOutlined
                      style={{ color: theme.palette.primary.main }}
                      className="delete-icon"
                      onClick={() => onRemove(index)}
                      data-testid={`remove-user-${index}`}
                    />
                  </StyledCheckboxItem>
                ))}
              {selectedUsers.length === 0 && (
                <StyledCheckboxItem key="empty" className="emptyuserblock">
                  <FormattedMessage id="UserGroup.list.placeholder" />
                </StyledCheckboxItem>
              )}
            </CheckboxContainer>
          </Col>
        </Row>

        <Row justify="center" style={{ marginTop: '30px' }}>
          <Space size="middle">
            <Button
              type="default"
              shape="round"
              size="large"
              onClick={() => cancel()}
              data-testid="group-cancel"
            >
              <FormattedMessage id="Global.cancel" />
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              shape="round"
              size="large"
              loading={loading}
              disabled={loading}
              data-testid="group-submit"
            >
              <FormattedMessage id="UserGroup.form.button.submit" />
            </Button>
          </Space>
        </Row>
      </Form>
      <SelectUserModal
        visible={isSelectUsersModal}
        userList={userList}
        selectedList={selectedUsers}
        onSelect={onSelect}
        onCloseModal={onCloseSelectUserModal}
      />
    </>
  );
};

export default AddGroupForm;
